"use client";

import React from "react";
import { Loader, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function MediaInquiryForm() {
  const {
    control,
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="232492" className="p-10 flex flex-col items-center justify-center text-center gap-4 min-h-[320px]">
        <div className="size-16 rounded-full flex items-center justify-center mb-2 shadow-lg shadow-primary/20" style={{
        background: "var(--color-primary)"
      }}>
          <Send className="size-7" style={{
          color: "var(--color-primary-foreground)"
        }} />
        </div>
        <h3 className="font-default text-xl font-bold text-white">
          Takk for din henvendelse!
        </h3>
        <p className="text-base max-w-md font-default text-slate-400">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-2 rounded-xl border-primary text-primary hover:bg-primary/5">
          Send en ny melding
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-7">
      {}
      <FormField control={control} name="name" rules={{
      required: "Vennligst oppgi navnet ditt."
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-sm font-semibold font-default text-white">
              Navn <span className="text-primary">*</span>
            </FormLabel>
            <FormControl>
              <Input type="text" placeholder="Ola Nordmann" className="h-11 px-4 rounded-xl text-sm transition-all focus:ring-2 focus:ring-primary/50 bg-secondary/50 border-white/10 text-white" {...field} />
            </FormControl>
            <FormDescription className="font-default text-slate-400">
              Skriv inn fullt navn.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      {}
      <FormField control={control} name="email" rules={{
      required: "E-postadresse er påkrevd.",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Ugyldig e-postadresse."
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-sm font-semibold font-default text-white">
              E‑postadresse <span className="text-primary">*</span>
            </FormLabel>
            <FormControl>
              <Input type="email" placeholder="ola@example.com" className="h-11 px-4 rounded-xl text-sm transition-all focus:ring-2 focus:ring-primary/50 bg-secondary/50 border-white/10 text-white" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {}
      <FormField control={control} name="phone" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-sm font-semibold font-default text-white">
              Telefonnummer{" "}
              <span className="text-xs font-normal text-slate-400">
                (valgfritt)
              </span>
            </FormLabel>
            <FormControl>
              <Input type="tel" placeholder="+47 123 45 678" className="h-11 px-4 rounded-xl text-sm transition-all focus:ring-2 focus:ring-primary/50 bg-secondary/50 border-white/10 text-white" {...field} />
            </FormControl>
            <FormDescription className="font-default text-slate-400">
              Valgfritt, men gjør det lettere å kontakte deg.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      {}
      <FormField control={control} name="package" rules={{
      required: "Vennligst velg en pakke."
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-sm font-semibold font-default text-white">
              Velg pakke <span className="text-primary">*</span>
            </FormLabel>
            <FormControl>
              <select className="h-11 w-full rounded-xl border px-4 text-sm outline-none transition-all focus:ring-2 focus:ring-primary/50 appearance-none cursor-pointer font-default bg-secondary/50 border-white/10 text-white" style={{
          borderColor: field.value ? "var(--color-primary)" : "rgba(255,255,255,0.1)",
          color: field.value ? "white" : "rgba(255,255,255,0.5)",
          "--tw-ring-color": "var(--color-primary)"
        }} {...field}>
                <option value="" disabled>
                  Velg en pakke
                </option>
                <option value="Standard Pakke">Standard Pakke</option>
                <option value="Proff Pakke">Proff Pakke</option>
                <option value="Ultimat Pakke">Ultimat Pakke</option>
              </select>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <Separator className="my-2 opacity-20 bg-white" />

      {}
      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-sm font-semibold font-default text-white">
              Tilleggsinformasjon
            </FormLabel>
            <FormControl>
              <Textarea placeholder="Har du spesielle ønsker eller spørsmål?" className="rounded-xl text-sm min-h-36 px-4 py-3 resize-y transition-all focus:ring-2 focus:ring-primary/50 font-default bg-secondary/50 border-white/10 text-white" {...field} />
            </FormControl>
            <FormDescription className="font-default text-slate-400">
              Fortell oss mer om dine behov.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 rounded-xl border text-sm font-default" style={{
      background: "hsl(0 84% 97%)",
      borderColor: "hsl(0 84% 90%)",
      color: "hsl(0 84% 40%)"
    }}>
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} size="lg" className="w-full h-12 text-base font-bold rounded-xl transition-all shadow-lg shadow-primary/25 hover:shadow-primary/40 active:scale-[0.98]" style={{
      background: "var(--color-primary)",
      color: "var(--color-primary-foreground)"
    }}>
        {isSubmitting ? <>
            <Loader className="animate-spin size-5 mr-2" />
            Sender forespørsel...
          </> : <>
            <Send className="size-5 mr-2" />
            Bestill nå
          </>}
      </Button>
    </Form>;
}
export default function InquiryForm() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.1
  });
  return;
}